import { useMemo } from "react";
import {
  Box,
  Card,
  CardBody,
  Text,
  Heading,
  Progress,
  Link,
  useColorModeValue,
  Skeleton,
} from "@chakra-ui/react";
import { Feature, Geometry } from "geojson";
import { Link as RouterLink } from "react-router-dom";

import { SceneFeature } from "../../../types/scenes";
import { getSortedSpecies } from "../../../utils";
import { Overline, SceneThumbnail, SceneStatusTag } from "../../../components";
import { SpeciesCount } from "../../../utils/inferenceStats";

type Props = {
  scene: Feature<Geometry, SceneFeature>;
  inferencesLoading: boolean | undefined;
  count: number;
  validated: number;
  species: SpeciesCount;
};

function SceneCard({
  scene,
  inferencesLoading,
  count,
  validated,
  species,
}: Props) {
  const { s3_prefix: id, date, model_runs } = scene.properties;

  const topSpecies = useMemo(() => {
    return getSortedSpecies(species);
  }, [species]);
  const hasSuccessModelRuns = model_runs.some(
    ({ status }) => status === "succeeded"
  );

  const cardBgColor = useColorModeValue("base.50", "base.400");

  return (
    <Card
      direction="row"
      overflow="hidden"
      position="relative"
      transition="all 0.24s ease"
      _hover={{ bg: cardBgColor, transform: "translateY(-2px)" }}
      _focus={{ boxShadow: "outline" }}
    >
      <Box width="80px" height="80px">
        <SceneThumbnail sceneId={id} width={80} height={80} />
      </Box>
      <CardBody display="flex" gap="4" p={3} px={4} alignItems="center">
        <Box flex="1" display="flex" flexDir="column" gap={1} height="100%">
          <Link
            as={RouterLink}
            to={`/scenes/${id}`}
            mb="auto"
            _hover={{ textDecoration: "none" }}
            _before={{
              content: "''",
              cursor: "inherit",
              display: "block",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 0,
              width: "100%",
              height: "100%",
            }}
          >
            <Heading as="h5" fontSize="md" lineHeight="1">
              {date.split("T")[0]}
            </Heading>
          </Link>
          {inferencesLoading ? (
            Array.from(Array(2)).map((i, ind) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={ind} display="flex" gap="1">
                <Skeleton height="2" width="3" />
                <Skeleton height="2" width="20" />
              </Box>
            ))
          ) : topSpecies.length > 0 ? (
            topSpecies.map(([label, value]) => (
              <Box
                key={label}
                flex="initial"
                lineHeight="1"
                as="dl"
                display="flex"
                gap="1"
              >
                <Overline as="dt" fontWeight="bold">
                  {value}
                </Overline>{" "}
                <Overline as="dd">{label.split("-")[0]}</Overline>
              </Box>
            ))
          ) : hasSuccessModelRuns ? (
            <Overline>
              <Text display="inline" fontWeight="bold">
                0{" "}
              </Text>
              Species Detected
            </Overline>
          ) : (
            <Overline>Retry model</Overline>
          )}
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap="2"
          flex="1"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <SceneStatusTag scene={scene} />
          {count > 0 && (
            <Box textAlign="center" width="100%">
              <Text fontSize="xs" color="base.200">
                {`Validated (${validated} / ${count})`}
              </Text>
              <Progress
                value={inferencesLoading ? 0 : (validated / count) * 100}
                width="100%"
                colorScheme="primary"
                size="sm"
                borderRadius="4"
              />
            </Box>
          )}
        </Box>
      </CardBody>
    </Card>
  );
}

export default SceneCard;
